import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { WebSocketService } from '../../web-socket-handlers/web-socket.service';
import { AssignmentType } from '../data-models/assignment-type.model';
import { DroneAssignment } from '../data-models/drone-assignment.model';
import { SettableDroneStatus } from '../data-models/settable-drone-status.model';
import { Drone } from '../data-models/drone.model';
import { CreateDroneRequest } from '../request-models/create-drone-request.model';
import { DroneRequest } from '../request-models/drone-request.model';
import { UpdateDroneLeaseRequest } from '../request-models/update-drone-lease-request.model';
import { OrganizationDrone } from '../response-models/organization-drone.model';
import { OrganizationDronesResponse } from '../response-models/organization-drones-response.model';
import {
  toAssignDroneRequest,
  toReturnDroneRequest,
  toRevokeDroneRequest,
  toUpdateDroneLeaseRequest,
} from '../utils/drone-assignment-request-utils';
import { toCreateDroneRequest, toDroneRequest } from '../utils/drone-request-utils';
import { OrganizationService } from './organization.service';

/**
 * Socket events after which the cached inventory list is refetched. The hub emits
 * `admin.drone_inventory_changed` (`{ droneId, change }`) to the admin-only `admins` room after every drone
 * and assignment write. The payload is never trusted: the list is always refetched over REST.
 */
export const DRONE_SOCKET_EVENTS: readonly string[] = ['admin.drone_inventory_changed'];

/** Bursts of socket events (an assign can emit several) within this window cause one refetch. */
export const DRONE_REFRESH_DEBOUNCE_MS = 300;

/**
 * ADN drone inventory and assignments: `/admin/drones` (hub `admin-drones-controller.ts` and
 * `admin-drone-assignments-controller.ts`). Also holds the cached inventory list as signals, with
 * `load()` / `refresh()` and a socket-driven refresh.
 */
@Injectable({ providedIn: 'root' })
export class DroneService extends BaseService<Drone> {
  private readonly organizationService = inject(OrganizationService);
  private readonly webSocketService = inject(WebSocketService);

  private readonly dronesSignal = signal<Drone[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly loadedSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  readonly drones = computed(() => this.dronesSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly isLoaded = computed(() => this.loadedSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    super('admin/drones', Drone);
    this.registerSocketRefresh(DRONE_SOCKET_EVENTS);
  }

  /** Loads the inventory list into `drones` once; later calls do nothing until `refresh()`. */
  async load(): Promise<void> {
    if (this.loadedSignal() || this.loadingSignal()) {
      return;
    }
    await this.refresh();
  }

  /** Refetches the inventory list into `drones`. A failure is kept in `error`; the old list stays. */
  async refresh(): Promise<void> {
    this.loadingSignal.set(true);
    try {
      this.dronesSignal.set(await firstValueFrom(this.getMany()));
      this.loadedSignal.set(true);
      this.errorSignal.set(null);
    } catch (err) {
      this.errorSignal.set(ApiError.fromHttpError(err).message);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** GET /admin/organizations/:orgId/drones: ADN-assigned and organization-owned drones. */
  getOrganizationDrones(organizationId: string): Observable<OrganizationDronesResponse> {
    return this.organizationService.getDrones(organizationId);
  }

  /** The organization's ADN-assigned and organization-owned drones as one array. */
  getOrganizationDronesFlat(organizationId: string): Observable<OrganizationDrone[]> {
    return this.getOrganizationDrones(organizationId).pipe(
      map(response => [...response.adnAssigned, ...response.organizationOwned])
    );
  }

  /** GET /admin/organizations/:orgId/drones?includeHistory=true: `adnAssigned` includes past assignments. */
  getOrganizationDroneHistory(organizationId: string): Observable<OrganizationDronesResponse> {
    return this.organizationService.getDrones(organizationId, true);
  }

  /** GET /admin/drones?status=available */
  getAvailableDrones(): Observable<Drone[]> {
    return this.getMany({ query: { status: 'available' } });
  }

  /** GET /admin/drones with optional filters (blank filters are not sent; `search` is trimmed). */
  getAllDrones(filters?: { status?: string; manufacturerId?: string; modelId?: string; search?: string }): Observable<Drone[]> {
    const query = {
      status: filters?.status || undefined,
      manufacturerId: filters?.manufacturerId || undefined,
      modelId: filters?.modelId || undefined,
      search: filters?.search?.trim() || undefined,
    };
    return this.getMany({ query });
  }

  /** GET /admin/drones/:id with manufacturer and model names. Emits `null` when the hub returns no data. */
  getDroneById(droneId: string): Observable<Drone | null> {
    return this.get(droneId);
  }

  /** POST /admin/drones with only the writable fields that are set. */
  createDrone(fields: CreateDroneRequest): Observable<Drone | null> {
    return this.create(toCreateDroneRequest(fields));
  }

  /** PUT /admin/drones/:id with only the writable fields that are set. Status changes use `updateStatus`. */
  updateDrone(droneId: string, fields: DroneRequest): Observable<Drone | null> {
    return this.update(droneId, toDroneRequest(fields));
  }

  /** DELETE /admin/drones/:id (the hub archives the drone). */
  archiveDrone(droneId: string): Observable<void> {
    return this.delete(droneId);
  }

  /** PUT /admin/drones/:id/status. 'leased' isn't settable here; it comes from `updateLease`. */
  updateStatus(droneId: string, status: SettableDroneStatus): Observable<Drone | null> {
    return this.request('PUT', { id: droneId, path: 'status' }, toDroneOrNull, { status });
  }

  /**
   * Takes a drone out of maintenance to 'assigned'. When its active assignment has a saved lease, the
   * lease is saved again so the hub sets the drone back to 'leased' (the status route can't set
   * 'leased'). Two calls, not one transaction: if the second fails the drone stays 'assigned' and
   * Edit Lease restores it. Resolves to the drone as the hub now has it.
   */
  async returnToService(droneId: string): Promise<Drone | null> {
    const drone = await firstValueFrom(this.updateStatus(droneId, 'assigned'));
    const { assignment } = await firstValueFrom(this.getCurrentAssignment(droneId));
    if (!assignment?.organizationId || assignment.assignmentType !== 'leased' || !assignment.leaseStatus) {
      return drone;
    }
    await firstValueFrom(this.updateLease(droneId, {
      organizationId: assignment.organizationId,
      assignmentType: 'leased',
      leaseStatus: assignment.leaseStatus,
      leaseStart: assignment.leaseStart,
      leaseEnd: assignment.leaseEnd,
      leaseTermination: assignment.leaseTermination,
      leaseNotes: assignment.leaseNotes,
    }));
    return firstValueFrom(this.getDroneById(droneId));
  }

  /** POST /admin/drones/:droneId/assign. Emits `null` when the hub returns no assignment. */
  assignDrone(
    droneId: string,
    organizationId: string,
    assignmentType: AssignmentType,
    assignedToProfileId?: string,
    notes?: string,
  ): Observable<DroneAssignment | null> {
    const body = toAssignDroneRequest({ organizationId, assignedToProfileId, assignmentType, notes });
    return this.request('POST', { id: droneId, path: 'assign' }, toAssignmentOrNull, body);
  }

  /**
   * POST /admin/drones/:droneId/lease: sets the active assignment's type and replaces its lease.
   * The hub also sets the drone to 'leased' (leased) or 'assigned' (demo). Emits `null` when the
   * hub returns no assignment.
   */
  updateLease(droneId: string, fields: UpdateDroneLeaseRequest): Observable<DroneAssignment | null> {
    return this.request('POST', { id: droneId, path: 'lease' }, toAssignmentOrNull, toUpdateDroneLeaseRequest(fields));
  }

  /** POST /admin/drones/:droneId/return. Emits `null` when the hub returns no assignment. */
  returnDrone(droneId: string, notes?: string): Observable<DroneAssignment | null> {
    return this.request('POST', { id: droneId, path: 'return' }, toAssignmentOrNull, toReturnDroneRequest({ notes }));
  }

  /** POST /admin/drones/:droneId/revoke. Emits `null` when the hub returns no assignment. */
  revokeAssignment(droneId: string, reason: string): Observable<DroneAssignment | null> {
    return this.request('POST', { id: droneId, path: 'revoke' }, toAssignmentOrNull, toRevokeDroneRequest({ reason }));
  }

  /** GET /admin/drones/:droneId/assignments */
  getDroneAssignmentHistory(droneId: string): Observable<DroneAssignment[]> {
    return this.request('GET', { id: droneId, path: 'assignments' }, toAssignments);
  }

  /** GET /admin/drones/:droneId/current-assignment. The hub sends the assignment or `null`. */
  getCurrentAssignment(droneId: string): Observable<{ assignment: DroneAssignment | null; hasActiveAssignment: boolean }> {
    return this.request('GET', { id: droneId, path: 'current-assignment' }, data => {
      const assignment = toAssignmentOrNull(data);
      return { assignment, hasActiveAssignment: assignment !== null };
    });
  }

  /**
   * On each of `events`, schedules one refetch of the cached list (bursts are coalesced), but only once
   * something has loaded it. The event payload is ignored: data only ever comes from REST.
   */
  protected registerSocketRefresh(events: readonly string[]): void {
    for (const event of events) {
      this.webSocketService.registerEventHandler(event, () => this.scheduleRefresh());
    }
  }

  /** Debounces socket-driven refreshes into one refetch per burst. */
  private scheduleRefresh(): void {
    if (!this.loadedSignal()) {
      return;
    }
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
    }
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refresh();
    }, DRONE_REFRESH_DEBOUNCE_MS);
  }
}

function toDroneOrNull(data: unknown): Drone | null {
  return data === null || data === undefined ? null : new Drone(data);
}

function toAssignmentOrNull(data: unknown): DroneAssignment | null {
  return data === null || data === undefined ? null : new DroneAssignment(data);
}

function toAssignments(data: unknown): DroneAssignment[] {
  return Array.isArray(data) ? data.map(item => new DroneAssignment(item)) : [];
}
